import _ from 'lodash';
import { MomentUtilities } from 'brock-date-time';
import { timeAndPrecisionTypes } from 'smartsuite-ui';
import {
    predicateValues,
    inputTypes,
    precisionMinutes,
} from '../constants/constants';
import {
    validateTimeAndPrecision,
    validateTimeRange,
} from './timeAndPrecisionValidation';

// eslint-disable-next-line complexity
export function isNumberInputValid(value) {
    return value !== undefined && value !== null && value !== '' && value > 0;
}

export function getRangeDomain(timeAndPrecision, ranges) {
    return ranges?.[timeAndPrecision.timeAndPrecisionType]?.[
        timeAndPrecision.rangeType
    ];
}

// eslint-disable-next-line complexity
export function isRangeValueWithinMax(timeAndPrecision, ranges) {
    const range =
        ranges?.[timeAndPrecision.timeAndPrecisionType]?.[
            timeAndPrecision.rangeType
        ];

    if (_.isEmpty(range) || !timeAndPrecision.rangeValue) {
        return true;
    }

    return timeAndPrecision.rangeValue <= range.max;
}

// eslint-disable-next-line complexity
export function isRangeValueWithinMin(timeAndPrecision, ranges) {
    const range =
        ranges?.[timeAndPrecision.timeAndPrecisionType]?.[
            timeAndPrecision.rangeType
        ];

    if (_.isEmpty(range) || !timeAndPrecision.rangeValue) {
        return true;
    }

    return timeAndPrecision.rangeValue >= range.min;
}

export function getIsFixedRangeInOrder(timeAndPrecision) {
    return (
        timeAndPrecision.timeAndPrecisionType !== timeAndPrecisionTypes.fixed ||
        MomentUtilities.dateIsBefore(
            timeAndPrecision.startDateTime,
            timeAndPrecision.endDateTime
        )
    );
}

// eslint-disable-next-line complexity
export function isFixedRangeWithinMin(timeAndPrecision, rangeDomains) {
    if (!rangeDomains?.fixed) {
        return true;
    }

    const typeMinutes = precisionMinutes[rangeDomains.fixed.precisionType];
    if (!typeMinutes) {
        throw Error('Range domain type is not configured');
    }

    return (
        timeAndPrecision.timeAndPrecisionType !== timeAndPrecisionTypes.fixed ||
        MomentUtilities.minutesDifference(
            timeAndPrecision.startDateTime,
            timeAndPrecision.endDateTime
        ) >=
            typeMinutes * rangeDomains.fixed.min
    );
}

// eslint-disable-next-line complexity
export function isFixedRangeWithinMax(timeAndPrecision, rangeDomains) {
    if (!rangeDomains?.fixed) {
        return true;
    }

    const typeMinutes = precisionMinutes[rangeDomains.fixed.precisionType];
    if (!typeMinutes) {
        throw Error('Range domain type is not configured');
    }

    return (
        timeAndPrecision.timeAndPrecisionType !== timeAndPrecisionTypes.fixed ||
        MomentUtilities.minutesDifference(
            timeAndPrecision.startDateTime,
            timeAndPrecision.endDateTime
        ) <=
            typeMinutes * rangeDomains.fixed.max
    );
}

// eslint-disable-next-line complexity
export function getIsFixedRangeValid(timeAndPrecision, rangeDomains) {
    return (
        timeAndPrecision.timeAndPrecisionType !== timeAndPrecisionTypes.fixed ||
        (isFixedRangeWithinMin(timeAndPrecision, rangeDomains) &&
            isFixedRangeWithinMax(timeAndPrecision, rangeDomains))
    );
}

// eslint-disable-next-line complexity
export function getIsLastNextRangeValid(timeAndPrecision, rangeDomains) {
    return (
        (timeAndPrecision.timeAndPrecisionType !== timeAndPrecisionTypes.last &&
            timeAndPrecision.timeAndPrecisionType !==
                timeAndPrecisionTypes.next) ||
        (isNumberInputValid(timeAndPrecision.rangeValue) &&
            isRangeValueWithinMax(timeAndPrecision, rangeDomains) &&
            isRangeValueWithinMin(timeAndPrecision, rangeDomains))
    );
}

export function getIsPrecisionValid(timeAndPrecision, isGauge) {
    return (
        isGauge ||
        (timeAndPrecision.precisionType !== '' &&
            isNumberInputValid(timeAndPrecision.precisionValue))
    );
}

export function filterHasValue(filter) {
    return filter && filter.values && filter.values.length > 0;
}

export function requiredFiltersHaveValues(
    requiredFilterKeys,
    requiredFilterValues,
    availableFilters,
    selectOptions = []
) {
    return requiredFilterKeys.every((requiredKey) => {
        const selectOption = selectOptions.find(
            (x) => x.fieldName === availableFilters?.[requiredKey].apiKey
        );

        if (!selectOption?.options.length) {
            throw Error(`Required filter: <${requiredKey}> has no options.`);
        }

        return (
            selectOption?.options.length === 1 ||
            filterHasValue(requiredFilterValues?.[requiredKey])
        );
    });
}

export function isRequiredFiltersHandled(
    requiredFilterKeys,
    filterValues,
    availableFilters,
    selectOptions = []
) {
    return (
        _.isEmpty(requiredFilterKeys) ||
        requiredFiltersHaveValues(
            requiredFilterKeys,
            filterValues,
            availableFilters,
            selectOptions
        )
    );
}

/* eslint-disable complexity,no-restricted-globals */
export function isWhereClauseValid(whereClause) {
    return (
        !whereClause.propertyName ||
        (whereClause.predicate &&
            whereClause.values[0] !== '' &&
            !isNaN(whereClause.values[0]))
    );
}
/* eslint-enable complexity,no-restricted-globals */

export function areWhereClausesValid(whereClauses) {
    return whereClauses.every(isWhereClauseValid);
}

/* eslint-disable no-restricted-globals */
export function isRecordLimitValid(recordLimitConfig) {
    return (
        recordLimitConfig.recordLimit === '' ||
        (!isNaN(recordLimitConfig.recordLimit) &&
            recordLimitConfig.recordLimit > 0)
    );
}

/* eslint-enable no-restricted-globals */
export function areTimeRangeFieldsValid(
    timeRange,
    timeRangeDomains,
    interpolateString,
    dashboardLabels
) {
    const isFixedRangeInOrder = getIsFixedRangeInOrder(timeRange);
    const isFixedRangeValid = getIsFixedRangeValid(timeRange, timeRangeDomains);
    const isLastNextRangeValid = getIsLastNextRangeValid(
        timeRange,
        timeRangeDomains
    );

    const { isTimeRangeValid } = validateTimeRange(
        timeRange,
        interpolateString,
        dashboardLabels
    );

    return (
        isFixedRangeInOrder &&
        isFixedRangeValid &&
        isLastNextRangeValid &&
        isTimeRangeValid
    );
}

// eslint-disable-next-line complexity
export function areTimeAndPrecisionFieldsValid(
    timeAndPrecision,
    timeAndPrecisionRangeDomains,
    showPrecisionConfig,
    interpolateString,
    dashboardLabels
) {
    const isFixedRangeInOrder = getIsFixedRangeInOrder(timeAndPrecision);
    const isFixedRangeValid = getIsFixedRangeValid(
        timeAndPrecision,
        timeAndPrecisionRangeDomains
    );
    const isLastNextRangeValid = getIsLastNextRangeValid(
        timeAndPrecision,
        timeAndPrecisionRangeDomains
    );
    const isPrecisionValid = getIsPrecisionValid(
        timeAndPrecision,
        !showPrecisionConfig
    );
    const { isTimeAndPrecisionValid } = validateTimeAndPrecision(
        timeAndPrecision,
        interpolateString,
        dashboardLabels,
        !showPrecisionConfig
    );

    return (
        isFixedRangeInOrder &&
        isFixedRangeValid &&
        isLastNextRangeValid &&
        isPrecisionValid &&
        isTimeAndPrecisionValid
    );
}

export function getFilterFieldClassModifiers(availableFilter) {
    let filterClassName = 'filter';

    if (availableFilter.cascadeChildKey) {
        filterClassName += ' filter--cascade-parent';
    }

    if (availableFilter.cascadeParentKey) {
        filterClassName += ' filter--cascade-child';
    }

    return filterClassName;
}

export function isACascadeFilter(availableFilter) {
    return (
        !!availableFilter.cascadeParentKey || !!availableFilter.cascadeChildKey
    );
}

export function getResetCascadeChildFilter(
    cascadeChildKey,
    filterValues,
    predicateLabels
) {
    if (!cascadeChildKey) {
        return {};
    }

    return {
        [cascadeChildKey]: {
            predicate: {
                textField: predicateLabels.isIn,
                valueField: predicateValues.isIn,
            },
            ...filterValues[cascadeChildKey],
            values: [],
        },
    };
}

export function anyBranchHasMultipleOptions(option) {
    if (option.children?.length > 1) {
        return true;
    }

    return option.children?.some(anyBranchHasMultipleOptions);
}

// eslint-disable-next-line complexity
export function shouldShowFilter(availableFilter, options, isRequired) {
    const hasCustomInput =
        availableFilter.type === inputTypes.unconstrainedMultiSelect ||
        availableFilter.type === inputTypes.string;

    const isTreeSelect = availableFilter.type === inputTypes.treeSelect;

    return !!(
        options.length > 1 ||
        hasCustomInput ||
        (isTreeSelect && options.some(anyBranchHasMultipleOptions)) ||
        (!isRequired && options.length)
    );
}

// eslint-disable-next-line complexity
function getParentFilterValueId(
    apiKey,
    filterKey,
    filterValues,
    selectOptions,
    requiredFilterKeys
) {
    const values = filterValues[filterKey]?.values;
    if (!_.isEmpty(values)) {
        return values[0].valueField;
    }

    if (!requiredFilterKeys.includes(filterKey)) {
        return null;
    }

    const selectOption = selectOptions.find((x) => x.fieldName === apiKey);
    return selectOption.options.length === 1
        ? selectOption.options[0].valueField
        : null;
}

// eslint-disable-next-line complexity
export function getFilterOptions(
    availableFilters,
    filterKey,
    filterValues,
    selectOptions,
    requiredFilterKeys
) {
    const filter = availableFilters[filterKey];
    const selectOption = selectOptions.find(
        (x) => x.fieldName === filter.apiKey
    );

    if (_.isEmpty(selectOption?.options)) {
        return [];
    }

    if (!filter.cascadeParentKey) {
        return selectOption.options;
    }

    const parentValue = getParentFilterValueId(
        availableFilters[filter.cascadeParentKey].apiKey,
        filter.cascadeParentKey,
        filterValues,
        selectOptions,
        requiredFilterKeys
    );

    return parentValue
        ? selectOption.options.filter((x) => x.cascadeParentId == parentValue) // eslint-disable-line eqeqeq
        : [];
}

function filterPredicateOptions(defaultPredicates, configPredicates) {
    const configPredicateValues = configPredicates.map((x) => x.valueField);

    return defaultPredicates.filter((predicate) =>
        configPredicateValues.includes(predicate.valueField)
    );
}

export function getPredicateOptions(predicateLabels, configPredicateOptions) {
    const defaultPredicateOptions = [
        {
            textField: predicateLabels.isIn,
            valueField: predicateValues.isIn,
        },
        {
            textField: predicateLabels.notIn,
            valueField: predicateValues.notIn,
        },
    ];

    if (
        !Array.isArray(configPredicateOptions) ||
        !configPredicateOptions.length
    ) {
        return defaultPredicateOptions;
    }

    return filterPredicateOptions(
        defaultPredicateOptions,
        configPredicateOptions
    );
}

export function getStringPredicateOptions(
    predicateLabels,
    configPredicateOptions
) {
    const defaultPredicateOptions = [
        {
            textField: predicateLabels.contains,
            valueField: predicateValues.contains,
        },
        {
            textField: predicateLabels.beginsWith,
            valueField: predicateValues.beginsWith,
        },
        {
            textField: predicateLabels.endsWith,
            valueField: predicateValues.endsWith,
        },
    ];

    if (
        !Array.isArray(configPredicateOptions) ||
        !configPredicateOptions.length
    ) {
        return defaultPredicateOptions;
    }

    return filterPredicateOptions(
        defaultPredicateOptions,
        configPredicateOptions
    );
}

export function getMeasurePredicateOptions(predicateLabels) {
    return [
        {
            textField: predicateLabels.is,
            valueField: predicateValues.is,
        },
        {
            textField: predicateLabels.lessThan,
            valueField: predicateValues.lessThan,
        },
        {
            textField: predicateLabels.lessThanOrEqualTo,
            valueField: predicateValues.lessThanOrEqualTo,
        },
        {
            textField: predicateLabels.greaterThan,
            valueField: predicateValues.greaterThan,
        },
        {
            textField: predicateLabels.greaterThanOrEqualTo,
            valueField: predicateValues.greaterThanOrEqualTo,
        },
    ];
}

export function getAlwaysVisibleFilterKeys(
    availableFilters = {},
    additionalFilterKeys = []
) {
    return Object.keys(availableFilters).filter(
        (filterKey) => !additionalFilterKeys.includes(filterKey)
    );
}

export function buildFilterWarning(filterName, dashboardLabels) {
    return `${filterName} ${dashboardLabels.filterSubsetWarning}`;
}

export function buildWidgetFilterWarning(
    filterKeys,
    availableFilters,
    dashboardLabels
) {
    return filterKeys.reduce((result, filterKey, index) => {
        const filterName = availableFilters[filterKey]?.label ?? filterKey;
        const filterWarning = `${filterName} ${dashboardLabels.widgetFilterSubsetWarning}`;

        if (index === filterKeys.length - 1) {
            return `${result}${filterWarning}`;
        }

        return `${result}${filterWarning}\n`;
    }, '');
}
